// React
import { useMemo } from 'react'

// Libraries
import { isEmpty } from 'lodash'

// Hooks
import { useFormikContext } from 'formik'
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { isLocationInputEnabled, isLocationSelectable as _isLocationSelectable } from 'ui/components/Picker/utils'
import { featureEnabled, FeatureFlags } from 'back_office/v2/utils/utils'

export const useLocationsData = ({
  locations,
  isSingleLocationSelect: singleLocationSelect,
  deliveryDistances,
  showDeliveryDistances: withDeliveryDistances,
  deliveryAddressPresent: withDeliveryAddress
}) => {
  const picker = usePicker()
  const form = useFormikContext()

  const isLocationSelectable = (options) => {
    return _isLocationSelectable({
      ...options,
      openingHours: picker.openingHours,
      values: form.values,
      locations
    })
  }

  const hasLocations = locations.size() > 1

  const startLocations = locations
  const stopLocations = useMemo(() => {
    return locations
      .filter((location) => {
        return isLocationSelectable({ type: 'stop', location })
      })
      .sort('name')
  }, [locations, isLocationSelectable])

  const options = useMemo(() => {
    const showDeliveryDistances =
      withDeliveryDistances && withDeliveryAddress && featureEnabled(FeatureFlags.DELIVERIES)

    if (showDeliveryDistances && !isEmpty(deliveryDistances)) {
      const locations = deliveryDistances
        .sort((a, b) => a.distance - b.distance)
        .map((distance) => {
          const location = startLocations.getById(distance.locationId)
          return { label: location.name, value: location.id }
        })

      return locations
    }

    return startLocations.sort('name').pluckObject({ label: 'name', value: 'id' })
  }, [startLocations, withDeliveryDistances, deliveryDistances, withDeliveryAddress])

  const isSingleLocationSelect = useMemo(() => {
    const hasStartInput = hasLocations && isLocationInputEnabled('start', { scope: picker.type })
    const hasStopInput = hasLocations && isLocationInputEnabled('stop', { scope: picker.type })

    return (
      singleLocationSelect ||
      (hasStartInput && !hasStopInput) ||
      (hasStartInput && hasStopInput && stopLocations?.size() <= 1)
    )
  }, [hasLocations, picker.type, singleLocationSelect, stopLocations])

  return {
    options,
    hasLocations,
    startLocations,
    stopLocations,
    isSingleLocationSelect
  }
}
